import { create } from 'zustand';
import { saveItem, listItems } from '../db'; 
import { NormalizationType , normalizationTypes} from '../utils/normalization';
import { AnalyzeWorkflowType, analyzeWorkflows, ShortenWorkflowType, shortenWorkflows,  FixingWorkflowType, fixingWorkflows } from '../utils/workflows';

type Settings = {
  apiUrl?: string;
  theme?: string;
  displayHiddenFragments?: boolean;
  normalizationType?: NormalizationType;
  analyzeWorkflowName?: AnalyzeWorkflowType;
  shortenWorkflowName?: ShortenWorkflowType;
  fixingWorkflowName?: FixingWorkflowType;
};

type SettingsStore = Settings & {
  setSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  loadSettings: () => Promise<void>;
  resetSettings: () => void;
  setTheme: (theme: string) => void;
  toggleTheme: () => void;
  toggleDisplayHiddenFragments: () => void;
  setApiUrl:  (url: string) => Promise<void>;
};

const defaultSettings: Partial<Settings> = {
    apiUrl: import.meta.env.VITE_MAIN_API_URL,
    theme: 'auto',
    displayHiddenFragments: false,
    normalizationType: normalizationTypes[0]?.value ?? import.meta.env.VITE_DEFAULT_NORMALIZATION_TYPE ?? '',
    analyzeWorkflowName: analyzeWorkflows[0]?.value ?? 'Analize',
    shortenWorkflowName: shortenWorkflows[0]?.value ?? 'Shortener',
    fixingWorkflowName: fixingWorkflows[0]?.value ?? 'Fixer'
};

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  ...defaultSettings,

  setSetting: (key, value) => {
    set({ [key]: value } as Partial<Settings>);
    saveItem('settings', { key, value }).catch(err =>
      console.error('Failed to save setting', key, err)
    );
  },

  loadSettings: async () => {
    console.log("loading settings")
    try {
      const items = await listItems('settings', 0, 100);
      const loaded: Partial<Settings> = {};
      items.forEach((item: any) => {
        loaded[item.key as keyof Settings] = item.value;
      });
      set(state => ({
        ...state,
        apiUrl: loaded.apiUrl ?? defaultSettings.apiUrl,
        theme: loaded.theme ?? defaultSettings.theme ?? 'auto',
        displayHiddenFragments: loaded.displayHiddenFragments ?? defaultSettings.displayHiddenFragments,
        normalizationType: loaded.normalizationType ?? defaultSettings.normalizationType,
        analyzeWorkflowName: loaded.analyzeWorkflowName ?? defaultSettings.analyzeWorkflowName,
        shortenWorkflowName: loaded.shortenWorkflowName ?? defaultSettings.shortenWorkflowName,
        fixingWorkflowName: loaded.fixingWorkflowName ?? defaultSettings.fixingWorkflowName
      }));
      const finalTheme = loaded.theme ?? defaultSettings.theme ?? 'auto';
      if (finalTheme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.classList.toggle('dark', prefersDark);
      } else {
        document.documentElement.classList.toggle('dark', finalTheme === 'dark');
      }
      console.log("API AFTER LOAD", loaded.apiUrl ?? defaultSettings.apiUrl);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  },

  resetSettings: () => {
    Object.entries(defaultSettings).forEach(([key, value]) => {
      saveItem('settings', { key, value }).catch(() =>
        console.warn('Failed to reset', key)
      );
    });
    set({ ...defaultSettings });
  },

  toggleDisplayHiddenFragments: () => {
    const current = get().displayHiddenFragments;
    const newValue = !current;
    set({ displayHiddenFragments: newValue });
    saveItem('settings', { key: 'displayHiddenFragments', value: newValue });
  },

  setTheme: (theme:string) => {
    set({ theme });
    saveItem('settings', { key: 'theme', value: theme });
    localStorage.setItem('theme', theme);
    if (theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    } else {
      document.documentElement.classList.toggle('dark', theme === 'dark');
    }
  },

  setApiUrl: async (url: string) => {
    set({ apiUrl: url });
    try {
      await saveItem('settings', { key: 'apiUrl', value: url });
      window.location.reload();
    } catch (err) {
      console.error('Failed to save apiUrl before reload:', err);
    }
  },
  
  toggleTheme: () => {
    const current = get().theme;
    const newTheme = current === 'dark' ? 'light' : 'dark';
    get().setTheme(newTheme);
  },
}));